import { authenticatedBlobFetch, authenticatedFetch } from "./http";

export type DocumentTemplateStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";
export type DocumentOutputFormat = "PDF" | "ZPL";

export interface DocumentTemplate {
  id: string;
  familyId: string;
  number: string;
  name: string;
  documentType: string;
  outputFormat: DocumentOutputFormat;
  outputConfiguration: Record<string, unknown>;
  dataContext: string | null;
  dictionaryVersion: number | null;
  language: string;
  availableLanguages: string[];
  status: DocumentTemplateStatus;
  template: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  archivedAt: string | null;
  version: number;
}

export interface DocumentDictionaryField {
  key: string;
  label: string;
  type: "TEXT" | "NUMBER" | "DATE" | "DATE_TIME" | "COLLECTION";
  required: boolean;
  example: string | null;
  columns: DocumentDictionaryField[];
}

export interface DocumentDictionary {
  context: string;
  version: number;
  label: string;
  fields: DocumentDictionaryField[];
}

export interface DocumentData {
  context: string;
  dictionaryVersion: number;
  filename: string;
  values: Record<string, string>;
}

export function loadDocumentDictionary(
  context: string, version: number
): Promise<DocumentDictionary> {
  return authenticatedFetch<DocumentDictionary>(
    `/api/v1/document-data/dictionaries/${context}/versions/${version}`
  );
}

export function loadInboundAsnDocumentData(asnId: string): Promise<DocumentData> {
  return authenticatedFetch<DocumentData>(`/api/v1/document-data/inbound-asns/${asnId}`);
}

export interface GeneratedDocument {
  id: string;
  number: string;
  templateId: string;
  templateNumber: string;
  templateVersion: number;
  dataContext: string;
  dictionaryVersion: number;
  entityType: string;
  entityId: string;
  entityNumber: string;
  filename: string;
  mediaType: "application/pdf" | "application/zpl";
  contentSize: number;
  contentSha256: string;
  generatedAt: string;
  generatedBy: string | null;
  generatedByEmail: string | null;
}

export function loadGeneratedAsnDocuments(asnId: string): Promise<GeneratedDocument[]> {
  return authenticatedFetch<GeneratedDocument[]>(
    `/api/v1/generated-documents/inbound-asns/${asnId}`
  );
}

export async function saveGeneratedAsnDocument(
  asnId: string, templateId: string, filename: string, blob: Blob
): Promise<GeneratedDocument> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return authenticatedFetch<GeneratedDocument>(
    `/api/v1/generated-documents/inbound-asns/${asnId}`,
    {
      method: "POST",
      body: JSON.stringify({ templateId, filename, contentBase64: btoa(binary) })
    }
  );
}

export function loadGeneratedAsnDocumentContent(
  asnId: string, documentId: string
): Promise<Blob> {
  return authenticatedBlobFetch(
    `/api/v1/generated-documents/inbound-asns/${asnId}/${documentId}/content`
  );
}

export function loadHandlingUnitDocuments(huId: string): Promise<GeneratedDocument[]> {
  return authenticatedFetch<GeneratedDocument[]>(
    `/api/v1/generated-documents/handling-units/${huId}`
  );
}

export function loadHandlingUnitDocumentContent(
  huId: string, documentId: string
): Promise<Blob> {
  return authenticatedBlobFetch(
    `/api/v1/generated-documents/handling-units/${huId}/${documentId}/content`
  );
}

export function loadInventoryCountDocuments(campaignId: string): Promise<GeneratedDocument[]> {
  return authenticatedFetch<GeneratedDocument[]>(
    `/api/v1/generated-documents/inventory-counts/${campaignId}`
  );
}

export function loadInventoryCountDocumentContent(
  campaignId: string, documentId: string
): Promise<Blob> {
  return authenticatedBlobFetch(
    `/api/v1/generated-documents/inventory-counts/${campaignId}/${documentId}/content`
  );
}

export interface DocumentAutomationJob {
  id: string;
  anchorCode: string;
  entityNumber: string;
  ruleNumber: string;
  ruleName: string;
  templateNumber: string;
  action: "GENERATE" | "GENERATE_AND_PRINT";
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  printStatus: "NOT_REQUESTED" | "PENDING" | "COMPLETED" | "FAILED";
  attempts: number;
  printerCode: string | null;
  generatedDocumentId: string | null;
  createdAt: string;
  completedAt: string | null;
  lastError: string | null;
}

export function loadDocumentAutomationJobs(): Promise<DocumentAutomationJob[]> {
  return authenticatedFetch<DocumentAutomationJob[]>("/api/v1/document-automation/jobs");
}

export interface DocumentAnchor {
  code: string;
  name: string;
  description: string;
  entityType: string;
}

export interface DocumentRule {
  id: string;
  number: string;
  name: string;
  anchorCode: string;
  templateId: string;
  templateNumber: string;
  templateName: string;
  templateLanguage: "fr" | "en";
  action: "GENERATE" | "GENERATE_AND_PRINT";
  copies: number;
  printerCode: string | null;
  priority: number;
  active: boolean;
  version: number;
}

export interface DocumentRuleMutation {
  name: string;
  anchorCode: string;
  templateId: string;
  action: "GENERATE" | "GENERATE_AND_PRINT";
  copies: number;
  printerCode: string | null;
  priority: number;
  active: boolean;
  version: number;
}

export function loadDocumentAnchors(): Promise<DocumentAnchor[]> {
  return authenticatedFetch<DocumentAnchor[]>("/api/v1/document-automation/anchors");
}

export function loadDocumentRules(): Promise<DocumentRule[]> {
  return authenticatedFetch<DocumentRule[]>("/api/v1/document-automation/rules");
}

export function createDocumentRule(request: DocumentRuleMutation): Promise<DocumentRule> {
  return authenticatedFetch<DocumentRule>("/api/v1/document-automation/rules", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateDocumentRule(id: string, request: DocumentRuleMutation): Promise<DocumentRule> {
  return authenticatedFetch<DocumentRule>(`/api/v1/document-automation/rules/${id}`, {
    method: "PUT", body: JSON.stringify(request)
  });
}

export function deleteDocumentRule(id: string, version: number): Promise<void> {
  return authenticatedFetch<void>(
    `/api/v1/document-automation/rules/${id}?version=${version}`, { method: "DELETE" }
  );
}

export interface DocumentTemplateMutation {
  name: string;
  documentType: string;
  language: string;
  outputFormat: DocumentOutputFormat;
  outputConfiguration: Record<string, unknown>;
  template: Record<string, unknown>;
  version?: number;
}

export interface DocumentTemplateTransferFile {
  schemaVersion: number;
  organization: string;
  exportedAt: string;
  families: Array<{
    templateNumber: string;
    displayName: string;
    documentType: string;
    variants: Array<{
      language: string;
      status: DocumentTemplateStatus;
      outputFormat?: DocumentOutputFormat;
      outputConfiguration?: Record<string, unknown>;
      template: Record<string, unknown>;
    }>;
  }>;
  automationRules?: Array<{
    ruleNumber: string;
    displayName: string;
    anchorCode: string;
    templateNumber: string;
    templateLanguage: string;
    action: "GENERATE" | "GENERATE_AND_PRINT";
    copies: number;
    printerCode: string | null;
    priority: number;
    conditions: Record<string, unknown>;
    active: boolean;
  }>;
}

export interface DocumentTemplateImportResult {
  added: number;
  updated: number;
  unchanged: number;
  rulesAdded: number;
  rulesUpdated: number;
  rulesUnchanged: number;
}

export function loadDocumentTemplates(): Promise<DocumentTemplate[]> {
  return authenticatedFetch<DocumentTemplate[]>("/api/v1/document-templates");
}

export function exportDocumentTemplates(includeAutomation: boolean): Promise<DocumentTemplateTransferFile> {
  return authenticatedFetch<DocumentTemplateTransferFile>(
    `/api/v1/document-templates/export?includeAutomation=${includeAutomation}`
  );
}

export function importDocumentTemplates(
  transfer: DocumentTemplateTransferFile
): Promise<DocumentTemplateImportResult> {
  return authenticatedFetch<DocumentTemplateImportResult>("/api/v1/document-templates/import", {
    method: "POST", body: JSON.stringify(transfer)
  });
}

export function createDocumentTemplate(
  request: DocumentTemplateMutation
): Promise<DocumentTemplate> {
  return authenticatedFetch<DocumentTemplate>("/api/v1/document-templates", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateDocumentTemplate(
  id: string, request: DocumentTemplateMutation
): Promise<DocumentTemplate> {
  return authenticatedFetch<DocumentTemplate>(`/api/v1/document-templates/${id}`, {
    method: "PUT", body: JSON.stringify(request)
  });
}

export function publishDocumentTemplate(
  id: string, version: number
): Promise<DocumentTemplate> {
  return authenticatedFetch<DocumentTemplate>(
    `/api/v1/document-templates/${id}/publish`, {
      method: "POST", body: JSON.stringify({ version })
    }
  );
}

export function archiveDocumentTemplate(
  id: string, version: number
): Promise<DocumentTemplate> {
  return authenticatedFetch<DocumentTemplate>(
    `/api/v1/document-templates/${id}/archive`, {
      method: "POST", body: JSON.stringify({ version })
    }
  );
}

export function duplicateDocumentTemplate(id: string): Promise<DocumentTemplate> {
  return authenticatedFetch<DocumentTemplate>(
    `/api/v1/document-templates/${id}/duplicate`, { method: "POST" }
  );
}

export function duplicateDocumentTemplateTranslation(
  id: string, request: { language: "fr" | "en" }
): Promise<DocumentTemplate> {
  return authenticatedFetch<DocumentTemplate>(
    `/api/v1/document-templates/${id}/translations`, {
      method: "POST", body: JSON.stringify(request)
    }
  );
}

export function deleteDocumentTemplate(id: string, version: number): Promise<void> {
  return authenticatedFetch<void>(
    `/api/v1/document-templates/${id}?version=${version}`, { method: "DELETE" }
  );
}
